import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { userKeys } from '../lib/queryKeys';

export interface UserLocation {
  id: string;
  userId: string;
  label: string;
  address: string;
  locationType: 'HOME' | 'OFFICE' | 'OTHER';
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserLocationInput {
  label: string;
  address: string;
  locationType?: 'HOME' | 'OFFICE' | 'OTHER';
  isDefault?: boolean;
}

export interface UpdateUserLocationInput {
  label?: string;
  address?: string;
  locationType?: 'HOME' | 'OFFICE' | 'OTHER';
  isDefault?: boolean;
  isActive?: boolean;
}

export function useUserLocations() {
  return useQuery<{ locations: UserLocation[] }>({
    queryKey: userKeys.locations(),
    queryFn: () => api.get('/profile/locations'),
  });
}

export function useCreateUserLocation() {
  const queryClient = useQueryClient();
  return useMutation<{ location: UserLocation }, Error, CreateUserLocationInput>({
    mutationFn: (data) => api.post('/profile/locations', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.locations() });
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

export function useUpdateUserLocation() {
  const queryClient = useQueryClient();
  return useMutation<{ location: UserLocation }, Error, { id: string; data: UpdateUserLocationInput }>({
    mutationFn: ({ id, data }) => api.put(`/profile/locations/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.locations() });
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}

export function useDeleteUserLocation() {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, string>({
    mutationFn: (id) => api.delete(`/profile/locations/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.locations() });
      void queryClient.invalidateQueries({ queryKey: userKeys.me() });
    },
  });
}
